import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, ImageOff, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { formatTime } from "@/lib/date-format";
import { cn } from "@/lib/utils";
import { errorMessage, fetchCaptures, PHOTO_SCALE, type StationCapture } from "./api";
import { MiniBadge } from "./mini-badge";
import { pizzaLabel } from "./products";
import { usePhoto, type PhotoState } from "./use-photo";

/** the photo, or what stands in for it while it loads or when it is not there */
export function PhotoView({ photo, alt, className }: { photo: PhotoState; alt: string; className?: string }) {
  const { t } = useLocalization();
  if (photo.kind === "ready") return <img src={photo.url} alt={alt} className={className} draggable={false} />;
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 bg-muted/40 p-4 text-center text-muted-foreground", className)}>
      {photo.kind === "loading" ? (
        <>
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          <span className="text-sm">{t("station.photo.loading")}</span>
        </>
      ) : photo.kind === "missing" ? (
        <>
          <ImageOff className="h-8 w-8" aria-hidden />
          <span className="text-sm">{t("station.photo.missing")}</span>
        </>
      ) : (
        <>
          <AlertOctagon className="h-8 w-8 text-red-400" aria-hidden />
          <span className="text-sm">{t("station.photo.failed", { error: photo.message })}</span>
        </>
      )}
    </div>
  );
}

/** One photo over the whole screen; a tap anywhere closes it. */
export function PhotoViewer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const { t } = useLocalization();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-50 flex flex-col bg-black" onClick={onClose}>
      <div className="flex h-16 shrink-0 items-center gap-3 px-4">
        <div className="min-w-0 flex-1 truncate text-lg font-semibold text-white">{title}</div>
        <Button variant="ghost" className="h-12 w-12 rounded-full p-0 text-white hover:bg-white/10" aria-label={t("station.photo.close")}>
          <X className="h-7 w-7" />
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-2 pt-0">{children}</div>
    </div>
  );
}

/** The branch's last photographed pizzas with their photos; a tap shows one over the whole screen. */
export function RecentPhotos({ onClose }: { onClose: () => void }) {
  const { t } = useLocalization();
  const [opened, setOpened] = useState<StationCapture | null>(null);
  const captures = useQuery({ queryKey: ["station", "captures"], queryFn: () => fetchCaptures(), staleTime: 0 });

  return (
    <div role="dialog" aria-modal="true" aria-label={t("station.recent.title")} className="fixed inset-0 z-40 flex flex-col bg-background">
      <header className="flex h-16 shrink-0 items-center gap-3 border-b bg-card px-4">
        <div className="min-w-0 flex-1 truncate text-lg font-semibold">{t("station.recent.title")}</div>
        <Button variant="ghost" className="h-12 w-12 rounded-full p-0" onClick={onClose} aria-label={t("station.photo.close")}>
          <X className="h-7 w-7" />
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 [overscroll-behavior:contain]">
        {captures.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" aria-hidden />
          </div>
        ) : captures.error ? (
          <p className="py-16 text-center text-base text-red-300">{t("station.recent.failed", { error: errorMessage(captures.error) })}</p>
        ) : !captures.data?.length ? (
          <p className="py-16 text-center text-base text-muted-foreground">{t("station.recent.none")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {captures.data.map((capture) => (
              <RecentCard key={capture.pizza_id} capture={capture} onOpen={() => setOpened(capture)} />
            ))}
          </div>
        )}
      </div>
      {opened && (
        <PhotoViewer title={captureTitle(opened)} onClose={() => setOpened(null)}>
          <ScreenPhoto pizzaId={opened.pizza_id} alt={captureTitle(opened)} />
        </PhotoViewer>
      )}
    </div>
  );
}

const captureTitle = (capture: StationCapture) => {
  const label = pizzaLabel(capture.name ?? "");
  return `${label.mini ? "MINI " : ""}${label.title} · ${formatTime(capture.capture_at)}`;
};

function ScreenPhoto({ pizzaId, alt }: { pizzaId: string; alt: string }) {
  const photo = usePhoto(pizzaId, PHOTO_SCALE.screen);
  return <PhotoView photo={photo} alt={alt} className="max-h-full max-w-full rounded-lg object-contain" />;
}

function RecentCard({ capture, onOpen }: { capture: StationCapture; onOpen: () => void }) {
  const photo = usePhoto(capture.pizza_id, PHOTO_SCALE.thumbnail);
  const label = pizzaLabel(capture.name ?? "");
  return (
    <button
      type="button"
      onClick={onOpen}
      className="overflow-hidden rounded-2xl border bg-card text-left shadow-sm transition-transform active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <PhotoView photo={photo} alt={label.title} className="aspect-[4/3] w-full object-cover" />
      <div className="flex items-center gap-2 px-3 py-2">
        {label.mini && <MiniBadge />}
        <span className="min-w-0 flex-1 truncate text-base font-medium">{label.title}</span>
        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{formatTime(capture.capture_at)}</span>
      </div>
    </button>
  );
}
